import { useContext } from 'react';
import {
  formatUnixTime,
  getWarrantyTerm,
  LabelledItemShape,
  parseId,
} from '@rabbit/elements/shared-components';
import { CaseflowContext } from '../../../../../context/CaseflowContext';
import { BL_Warranty } from '@rabbit/bizproc/core';
import { OurDateTime } from '@rabbit/utils/ts';
import { useTranslation } from 'react-i18next';
import { formatDate, stripTags } from '@rabbit/sage/utils/helpers';
import { AppContext } from '@rabbit/app-context';
import { WarrantyStatus } from '@rabbit/data/types';

export interface ClaimDetailsSectionType2Props {
  renderFns: {
    renderCaseFiles: () => JSX.Element;
    renderShopifyOrderLink: () => JSX.Element;
    renderSerialProof: () => JSX.Element;
    renderConsumerIssueDesc: () => JSX.Element;
    renderClaimDetailItemsLine: (
      claimDetailItems: LabelledItemShape[],
      keys: string[],
      extraClasses?: string
    ) => JSX.Element;
  };
  faultLabels: string[];
}

export function ClaimDetailsSectionType2(props: ClaimDetailsSectionType2Props) {
  const { faultLabels } = props;
  const {
    renderCaseFiles,
    renderShopifyOrderLink,
    renderSerialProof,
    renderClaimDetailItemsLine,
  } = props.renderFns;
  const { caseFacts, holdingData, holdingProxyData, caseId, caseTimeCreated } =
    useContext(CaseflowContext) || {};

  const { config } = useContext(AppContext);

  const { t } = useTranslation();
  // const { config } = useContext(ConfigContext);
  const latestWarranty = BL_Warranty.getLatestWarranty(
    holdingProxyData?.warranties
  );
  const warrantyInfo = holdingData?.warranties?.[0] || latestWarranty;

  const isLifetimeWarranty = () => {
    if (!warrantyInfo) return false;
    // Check if endDate is -1 (lifetime warranty indicator)
    if (warrantyInfo.endDate === -1) return true;
    // Check if duration amount is >= 1188 months (99 years)
    if (warrantyInfo.duration?.amount && warrantyInfo.duration.amount >= 1188)
      return true;
    // Check if appliedOptions has P99Y duration
    const durationOption = warrantyInfo.appliedOptions?.find(
      (opt: any) => opt.key === 'duration'
    );
    if (durationOption?.value === 'P99Y') return true;
    // Check if getWarrantyTerm returns "Lifetime"
    if (holdingData && getWarrantyTerm(holdingData) === 'Lifetime') return true;
    return false;
  };

  const isWithinWarranty = () => {
    if (!holdingProxyData) return '-';

    if (!latestWarranty || !latestWarranty.endDate) return '-';

    return OurDateTime.nowUTCTimestamp() < latestWarranty?.endDate
      ? 'Yes'
      : 'No';
  };

  /* --------------------------- Claim detail items --------------------------- */

  const claimDetailItems: LabelledItemShape[] = [
    {
      key: 'warranty_status',
      label: t('general.warrantyStatus'),
      value: caseFacts?.warranty_status ?? '-',
    },
    {
      key: 'claim_id',
      label: t('general.claimId'),
      value: caseId
        ? `${
            caseFacts?.warranty_status
              ? caseFacts?.warranty_status.toLowerCase() === 'in warranty'
                ? 'WR-'
                : 'OR-'
              : ''
          }${parseId(caseId)}`
        : '-',
    },
    ...(config.WARRANTIES.MANAGEMENT.CAN_SET_PART_STATUS
      ? [
          {
            key: 'part_status',
            label: t('general.part_status'),
            value: caseFacts?.part_status ?? '-',
          },
        ]
      : []),
    {
      key: 'date_of_claim',
      label: t('general.dateOfClaim'),
      value: caseTimeCreated
        ? formatUnixTime(Number(caseTimeCreated), 'dd/MM/yyyy')
        : '-',
    },
    {
      key: 'purchase_date',
      label: t('general.purchaseDate'),
      value:
        holdingData?.purchase_time && holdingData?.purchase_time > 0
          ? formatUnixTime(holdingData?.purchase_time, 'dd/MM/yyyy')
          : '-',
    },
    {
      key: 'warranty_term',
      label: t('general.warrantyTerm'),
      value: holdingData ? `${getWarrantyTerm(holdingData)}` : '-',
    },
    {
      key: 'warranty_expiry',
      label: t('general.warrantyExpiry'),
      value: (() => {
        if (warrantyInfo?.status === WarrantyStatus.VOIDED)
          return t('general.voided');
        if (isLifetimeWarranty()) return '-';
        if (warrantyInfo?.endDate)
          return formatDate(new Date(warrantyInfo.endDate));
        return '-';
      })(),
    },
    {
      key: 'within_warranty_period',
      label: t('general.withinWarrantyPeriod'),
      value: isWithinWarranty(),
    },
    {
      key: 'customer_issue_type',
      label: t('general.customerIssueType'),
      value: caseFacts?.consumer_issue_type ?? '-',
    },
    {
      key: 'proof_of_purchase',
      label: t('general.proofOfPurchaseSupportingMaterials'),
      value: renderCaseFiles(),
    },
    ...(holdingProxyData?.shopifyLinks?.orderId &&
    holdingProxyData?.shopifyLinks?.shopId
      ? [
          {
            key: 'shopify_order',
            label: t('general.orderDetails'),
            value: renderShopifyOrderLink(),
          },
        ]
      : []),
    ...(config.CLAIMS.CLAIMS_FLOW.NEW_CLAIM.MILEAGE
      ? [
          {
            key: 'mileage',
            label: t('general.mileage'),
            value: caseFacts?.mileage
              ? `${Number(caseFacts.mileage.value).toLocaleString('en')} ${
                  caseFacts.mileage.unit
                }`
              : `-`,
          },
        ]
      : []),
    ...(config.CLAIMS.CLAIMS_FLOW.NEW_CLAIM.SERIAL_NUMBER
      ? [
          {
            key: 'serial_number',
            label: t('general.serialNumber'),
            value:
              (caseFacts?.consumer_holding_serial_number
                ? caseFacts.consumer_holding_serial_number
                : caseFacts?.serial_number
                ? caseFacts.serial_number
                : '-') + ' ',
          },
        ]
      : []),
    ...(config.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER_PROOF
      ? [
          {
            key: 'serial_number_proof',
            label: t('general.serialNumberProof'),
            value: renderSerialProof(),
          },
        ]
      : []),
    {
      key: 'customer_issue_description',
      label: t('general.customerIssueDescription'),
      value: stripTags(caseFacts?.consumer_issue_description ?? '') ?? '-',
      className: 'col-span-full',
    },
  ];

  /* -------------------------------- Main TSX -------------------------------- */
  return (
    <div>
      {renderClaimDetailItemsLine(claimDetailItems, [
        'claim_id',
        'warranty_status',
        ...(config.WARRANTIES.MANAGEMENT.CAN_SET_PART_STATUS
          ? ['part_status']
          : []),
        'date_of_claim',
        'purchase_date',
        ...(!config.WARRANTIES.MANAGEMENT.CAN_SET_PART_STATUS
          ? ['warranty_term']
          : []),
      ])}
      {renderClaimDetailItemsLine(claimDetailItems, [
        ...(config.WARRANTIES.MANAGEMENT.CAN_SET_PART_STATUS
          ? ['warranty_term']
          : []),
        'warranty_expiry',
        'customer_issue_type',
        'customer_issue_description',
        'within_warranty_period',
        'proof_of_purchase',
      ])}
      {renderClaimDetailItemsLine(
        claimDetailItems,
        ['shopify_order', 'serial_number', 'serial_number_proof', 'mileage'],
        'mb-0'
      )}
    </div>
  );
}

export default ClaimDetailsSectionType2;
